import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, MessageSquare } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { CommunityVoteBar } from '@/components/community/CommunityVoteBar'
import { formatShortTimeAgo } from '@/lib/communityTimeBuckets'
import {
  createCommunityComment,
  fetchCommunityCommentsPage,
  fetchCommunityVoteDirectionsForCommentsAndUser,
  setCommunityCommentVote,
  type CommunityComment,
} from '@/lib/communityComments'
import type { CommunityVoteDirection } from '@/lib/communityPostData'

interface CommunityCommentThreadProps {
  readonly postId: string
  readonly viewerUserId: string | null
  readonly authorDisplayNameForViewer: string | null
  readonly onCommentCountChange?: () => void
}

function CommentRow({
  comment,
  viewerDirection,
  onPressVote,
  onPressReply,
  isReplying,
  replyDraft,
  onReplyDraftChange,
  onSubmitReply,
  submittingReply,
  children,
}: {
  comment: CommunityComment
  viewerDirection: CommunityVoteDirection | null
  onPressVote: (direction: CommunityVoteDirection) => void
  onPressReply: () => void
  isReplying: boolean
  replyDraft: string
  onReplyDraftChange: (value: string) => void
  onSubmitReply: () => void
  submittingReply: boolean
  children?: React.ReactNode
}) {
  const { t } = useTranslation()

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div style={{ marginLeft: `${Math.min(comment.depth, 6) * 12}px` }} className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground/80">
              {comment.authorDisplayName?.trim() || String(t('community.anonymousHandle'))}
            </span>
            <span aria-hidden>·</span>
            <span>{formatShortTimeAgo(comment.createdAt)}</span>
          </div>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{comment.body}</p>
          <div className="flex items-center gap-3">
            <CommunityVoteBar
              layout="inline"
              viewerDirection={viewerDirection}
              netScore={comment.score}
              commentCount={comment.replyCount}
              onPressUpvote={() => onPressVote('up')}
              onPressDownvote={() => onPressVote('down')}
              onPressComments={comment.replyCount > 0 ? undefined : onPressReply}
            />
            <button
              type="button"
              className="text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={onPressReply}
            >
              {t('community.detail.replyAction', 'Reply')}
            </button>
          </div>
          {isReplying ? (
            <div className="flex items-end gap-2 pt-1">
              <textarea
                rows={2}
                value={replyDraft}
                onChange={(event) => onReplyDraftChange(event.target.value)}
                placeholder={String(t('community.detail.replyPlaceholder', 'Write a reply…'))}
                className="min-h-[42px] flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm"
              />
              <Button type="button" size="sm" disabled={submittingReply || replyDraft.trim().length === 0} onClick={onSubmitReply}>
                {t('community.detail.sendCommentAria', 'Send')}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  )
}

function CommentBranch({
  comment,
  postId,
  viewerUserId,
  authorDisplayNameForViewer,
  voteDirectionByCommentId,
  onVote,
  onCommentCountChange,
}: {
  comment: CommunityComment
  postId: string
  viewerUserId: string | null
  authorDisplayNameForViewer: string | null
  voteDirectionByCommentId: ReadonlyMap<string, CommunityVoteDirection>
  onVote: (commentId: string, direction: CommunityVoteDirection) => void
  onCommentCountChange?: () => void
}) {
  const [repliesExpanded, setRepliesExpanded] = useState(false)
  const [replies, setReplies] = useState<CommunityComment[]>([])
  const [loadingReplies, setLoadingReplies] = useState(false)
  const [replying, setReplying] = useState(false)
  const [replyDraft, setReplyDraft] = useState('')
  const [submittingReply, setSubmittingReply] = useState(false)

  const loadReplies = useCallback(async () => {
    setLoadingReplies(true)
    try {
      const page = await fetchCommunityCommentsPage({
        postId,
        parentCommentId: comment.id,
      })
      setReplies(page.comments)
    } finally {
      setLoadingReplies(false)
    }
  }, [comment.id, postId])

  useEffect(() => {
    if (repliesExpanded && replies.length === 0) {
      void loadReplies()
    }
  }, [loadReplies, replies.length, repliesExpanded])

  const handleSubmitReply = async () => {
    if (!viewerUserId || replyDraft.trim().length === 0) return
    setSubmittingReply(true)
    try {
      const createdReply = await createCommunityComment({
        postId,
        parentCommentId: comment.id,
        userId: viewerUserId,
        authorDisplayName: authorDisplayNameForViewer,
        body: replyDraft,
      })
      setReplyDraft('')
      setReplying(false)
      setRepliesExpanded(true)
      setReplies((current) => [...current, createdReply])
      onCommentCountChange?.()
    } finally {
      setSubmittingReply(false)
    }
  }

  return (
    <CommentRow
      comment={comment}
      viewerDirection={voteDirectionByCommentId.get(comment.id) ?? null}
      onPressVote={(direction) => onVote(comment.id, direction)}
      onPressReply={() => setReplying((current) => !current)}
      isReplying={replying}
      replyDraft={replyDraft}
      onReplyDraftChange={setReplyDraft}
      onSubmitReply={() => void handleSubmitReply()}
      submittingReply={submittingReply}
    >
      {comment.replyCount > 0 ? (
        <div style={{ marginLeft: `${Math.min(comment.depth + 1, 6) * 12}px` }}>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary"
            onClick={() => setRepliesExpanded((current) => !current)}
          >
            {repliesExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {comment.replyCount} {comment.replyCount === 1 ? 'reply' : 'replies'}
          </button>
        </div>
      ) : null}
      {repliesExpanded ? (
        <div className="space-y-4 pt-2">
          {loadingReplies ? <div className="h-10 animate-pulse rounded bg-muted/40" /> : null}
          {replies.map((reply) => (
            <CommentBranch
              key={reply.id}
              comment={reply}
              postId={postId}
              viewerUserId={viewerUserId}
              authorDisplayNameForViewer={authorDisplayNameForViewer}
              voteDirectionByCommentId={voteDirectionByCommentId}
              onVote={onVote}
              onCommentCountChange={onCommentCountChange}
            />
          ))}
        </div>
      ) : null}
    </CommentRow>
  )
}

export function CommunityCommentThread({
  postId,
  viewerUserId,
  authorDisplayNameForViewer,
  onCommentCountChange,
}: CommunityCommentThreadProps) {
  const { t } = useTranslation()
  const [topLevelComments, setTopLevelComments] = useState<CommunityComment[]>([])
  const [loading, setLoading] = useState(true)
  const [voteDirectionByCommentId, setVoteDirectionByCommentId] = useState<
    ReadonlyMap<string, CommunityVoteDirection>
  >(new Map())

  const refreshTopLevelComments = useCallback(async () => {
    setLoading(true)
    try {
      const page = await fetchCommunityCommentsPage({ postId, parentCommentId: null })
      setTopLevelComments(page.comments)
    } finally {
      setLoading(false)
    }
  }, [postId])

  useEffect(() => {
    void refreshTopLevelComments()
  }, [refreshTopLevelComments])

  useEffect(() => {
    if (!viewerUserId || topLevelComments.length === 0) {
      setVoteDirectionByCommentId(new Map())
      return
    }

    let cancelled = false
    void fetchCommunityVoteDirectionsForCommentsAndUser({
      postId,
      commentIds: topLevelComments.map((comment) => comment.id),
      userId: viewerUserId,
    }).then((directions) => {
      if (!cancelled) setVoteDirectionByCommentId(directions)
    })

    return () => {
      cancelled = true
    }
  }, [postId, topLevelComments, viewerUserId])

  const handleVote = async (commentId: string, direction: CommunityVoteDirection) => {
    if (!viewerUserId) return
    const { resultingDirection } = await setCommunityCommentVote({
      postId,
      commentId,
      userId: viewerUserId,
      pressedDirection: direction,
    })
    setVoteDirectionByCommentId((current) => {
      const next = new Map(current)
      if (resultingDirection == null) next.delete(commentId)
      else next.set(commentId, resultingDirection)
      return next
    })
    await refreshTopLevelComments()
  }

  if (loading && topLevelComments.length === 0) {
    return <div className="h-20 animate-pulse rounded-lg bg-muted/40" />
  }

  if (topLevelComments.length === 0) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <MessageSquare className="h-4 w-4" />
        <span>{t('community.detail.commentsEmpty')}</span>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {topLevelComments.map((comment) => (
        <CommentBranch
          key={comment.id}
          comment={comment}
          postId={postId}
          viewerUserId={viewerUserId}
          authorDisplayNameForViewer={authorDisplayNameForViewer}
          voteDirectionByCommentId={voteDirectionByCommentId}
          onVote={(commentId, direction) => void handleVote(commentId, direction)}
          onCommentCountChange={() => {
            void refreshTopLevelComments()
            onCommentCountChange?.()
          }}
        />
      ))}
    </div>
  )
}
